// @flow

// external libs
import { pathOr } from 'ramda'
import { createReducer, createActions } from 'reduxsauce'
import Immutable from 'seamless-immutable'

import { generateTextsFromSpecification } from './ProductRedux'

// config
import { config } from '../../Config/ConfigLoader'

import { createQuoteRequestData } from '../ContainerFunctions'

// types
import type FieldValidator from '../FieldValidator'
import type { QuoteState, SubscribeType } from './Types/QuoteStateType'
import type { ExpandedSpecificationDTO } from '../Types/Field'
import type { ProductContent } from '../Types/ProductContent'
import type { Sort } from '../Types/Sort'
import type { QuoteFilterBlockType } from '../Types/FilterTypes'
import type { QuoteAsyncRequestSerializerType } from '../Serializers/QuoteAsyncRequestSerializer'
import type { DynamicPropertiesType, DynamicPropertiesRuleType } from '../Types/TariffsContentType'

import { filteredDataSort } from '../SortFunctions.js'
import { getValueForPath } from '../FilterFunctions'
import type QuoteInstance from '../Controllers/QuoteInstance'

/* ------------- Types and Action Creators ------------- */

const { Types, Creators } = createActions({
  quoteAsyncRequest: ['product', 'data', 'quotesKey', 'dependsOn', 'subProductName', 'linkData'],
  quoteAsyncRequestSuccess: ['expectedQuotes', 'asyncRequestId', 'quotesKey'],
  getQuoteRequest: ['data', 'quotesKey', 'calculatorName', 'oldId'],
  refreshQuoteRequestSuccess: ['newQuote', 'quotesKey'],
  getQuoteSupplementaryInsurancePriceRequest: ['data', 'quote', 'backupData'],
  getQuoteSupplementaryInsurancePriceRequestSuccess: ['newQuote', 'oldQuote'],
  sortTabQuotes: ['activeTabKey', 'quotes'],
  sortQuotes: ['quotes', 'quotesKey', 'path'],
  setSort: ['sort'],
  removeSort: ['sort'],
  saveQuotes: ['quotes', 'quotesKey', 'invalid'],
  removeQuote: ['id', 'quotesKey'],
  resetQuotes: ['isBackOffice'],
  quoteSetValidator: ['calculatorName', 'validator', 'productDescriptorValues'],
  quoteSetSpecification: ['quoteId', 'specification'],
  quoteContentRequest: ['productName'],
  quoteContentRequestSuccess: ['quoteContent'],
  onChangeActiveTab: ['activeTabKey'],
  onChangeGoToTab: ['goToTab'],
  onSelectQuote: ['productName', 'quote'],
  onRemoveQuote: ['productName', 'quoteId'],
  setFilter: ['tab', 'name', 'value', 'multiSelect', 'temporary'],
  resetFilters: null,
  getCoverageLimitsRequest: ['calculatorName', 'isBackOffice'],
  getCoverageLimitsRequestSuccess: ['calculatorName', 'data'],
  getAllCoverageLimitsRequest: ['isBackOffice'],
  getAllCoverageLimitsRequestSuccess: ['data'],
  changeStompSubscribed: ['subscribed', 'quoteKey'],
  setToUnsubscribe: ['subscribed'],
  setStompConnected: ['stompConnected'],
  onAddToEmailQuotes: ['quote'],
  onRemoveFromEmailQuotes: ['index'],
  setQuoteRestrictions: ['quoteRestrictions'],
  setQuoteFilters: ['filters', 'key'],
  setQuoteRequestDataCalculators: ['enabledCalculators', 'quoteKeys'],
  setSelectedQuotes: ['selectedQuotes'],
  clearCurrentQuoteData: ['quoteKeys', 'tabKey'],
  setSignalToChangeTab: ['tab'],
  resetLastSupplementaryInsuranceRequestData: ['quoteId', 'data'],
  setQuoteRequests: ['quoteRequests'],
  setSelectedCoveragePrices: ['selectedCoveragePrices'],
})

export const QuoteTypes = Types
export default Creators

/* ------------- Helpers ------------- */

const checkOneRule = (rule: DynamicPropertiesRuleType, formData: Object, operator: 'and' | 'or' | 'not' = 'and') => {
  if (typeof rule === 'string') {
    const value = getValueForPath(rule, 0, formData)
    return typeof value !== 'undefined' && !!value
  } else if (Array.isArray(rule)) {
    return rule.reduce((acc, ruleItem) => {
      if (operator === 'and') {
        return acc && checkOneRule(ruleItem, formData, operator)
      } else if (operator === 'or') {
        return acc || checkOneRule(ruleItem, formData, operator)
      } else if (operator === 'not') {
        return acc && !checkOneRule(ruleItem, formData, operator)
      }
      return acc
    }, operator === 'and' || operator === 'not')
  } else if (typeof rule === 'object' && rule.path && rule.value) {
    const value = getValueForPath(rule.path, 0, formData)
    return value === rule.value
  } else if (typeof rule === 'object') {
    return Object.keys(rule).reduce((acc, op) => acc && checkOneRule(rule[op], formData, op), true)
  }
  return false
}

export const initDynamicProperties = (dynamicProperties: DynamicPropertiesType, formData: Object) =>
  Object.keys(dynamicProperties).reduce((acc, key) => {
    acc[key] = checkOneRule(dynamicProperties[key], formData)
    return acc
  }, {})

/* ------------- Initial State ------------- */

export const INITIAL_STATE: QuoteState = Immutable({
  quoteRequests: {
    default: {
      expectedQuotes: 0,
      asyncRequestId: '',
      dependsOnQuoteId: '',
      quoteRequestData: {
        enabledCalculators: [],
        selectedTariffs: {},
        useAllTariffsIfEmpty: config.getConfig('productDetailContainer.useAllTariffsIfEmpty'),
      },
      currentQuoteRequestData: {},
      invalidQuotes: [],
      quotes: [],
      subscribed: null,
    },
    tplCic: {
      expectedQuotes: 0,
      asyncRequestId: '',
      dependsOnQuoteId: '',
      quoteRequestData: {
        enabledCalculators: [],
        selectedTariffs: {},
        useAllTariffsIfEmpty: {
          tpl: true,
          cic: true,
        },
      },
      currentQuoteRequestData: {},
      quotes: [],
      subscribed: null,
    },
    tpl: {
      expectedQuotes: 0,
      asyncRequestId: '',
      dependsOnQuoteId: '',
      quoteRequestData: {
        enabledCalculators: [],
        selectedTariffs: {},
        useAllTariffsIfEmpty: {
          tpl: true,
        },
      },
      currentQuoteRequestData: {},
      quotes: [],
      subscribed: null,
    },
    cic: {
      expectedQuotes: 0,
      asyncRequestId: '',
      dependsOnQuoteId: '',
      quoteRequestData: {
        enabledCalculators: [],
        selectedTariffs: {},
        useAllTariffsIfEmpty: {
          cic: true,
        },
      },
      currentQuoteRequestData: {},
      quotes: [],
      subscribed: null,
    },
  },
  quoteValidators: {},
  quoteSpecifications: {},
  quoteContent: {},
  quoteTexts: {},
  activeTabKey: config.getConfig('quoteListContainer.quoteDefaultKey'),
  goToTab: '',
  selectedQuotes: {},
  activeSorts: {
    default: config.getConfig('quoteListContainer.defaultSorts'),
    tpl: config.getConfig('quoteListContainer.defaultSorts'),
    cic: config.getConfig('quoteListContainer.defaultSorts'),
    tplCic: config.getConfig('quoteListContainer.defaultSorts'),
  },
  filters: {},
  temporaryFilters: {},
  coverageLimits: {},
  allSubscriptions: {},
  toUnsubscribe: {},
  stompConnected: false,
  basePrices: {},
  lastSupplementaryInsuranceRequestData: '{}',
  sendToEmailQuotes: [],
  selectedCoveragePrices: {},
  quoteRestrictions: { isCallback: [], priceHidden: [] },
  signalToChangeTab: '',
  timeAsyncRequest: {},
})

/* ------------- Reducers ------------- */

const resetQuotesR = (state: QuoteState, { isBackOffice }: { isBackOffice: boolean }): QuoteState =>
  state.merge({
    quoteRequests: INITIAL_STATE.quoteRequests,
    quoteValidators: INITIAL_STATE.quoteValidators,
    quoteSpecifications: INITIAL_STATE.quoteSpecifications,
    selectedQuotes: isBackOffice ? state.selectedQuotes : INITIAL_STATE.selectedQuotes,
    selectedCoveragePrices: INITIAL_STATE.selectedCoveragePrices,
    basePrices: INITIAL_STATE.basePrices,
    lastSupplementaryInsuranceRequestData: INITIAL_STATE.lastSupplementaryInsuranceRequestData,
  })

const quoteAsyncRequestR = (
  state: QuoteState,
  { quotesKey, data }: { quotesKey: string, data: QuoteAsyncRequestSerializerType },
) =>
  state.setIn(['quoteRequests', quotesKey, 'currentQuoteRequestData'], {
    ...state.getIn(['quoteRequests', quotesKey, 'quoteRequestData'], {}),
    enabledCalculators: data.enabledCalculators,
    selectedTariffs: data.selectedTariffs,
    useAllTariffsIfEmpty: data.useAllTariffsIfEmpty,
  })

const changeStompSubscribedR = (
  state: QuoteState,
  { subscribed, quoteKey }: { subscribed: null | SubscribeType, quoteKey: string },
) => state.setIn(['quoteRequests', quoteKey, 'subscribed'], subscribed)

const setToUnsubscribeR = (state: QuoteState, { subscribed }: { subscribed: SubscribeType }) =>
  state.setIn(['toUnsubscribe', subscribed.id], subscribed)

const setStompConnectedR = (state: QuoteState, { stompConnected }: { stompConnected: boolean }) =>
  state.set('stompConnected', stompConnected)

const quoteAsyncRequestSuccessR = (
  state: QuoteState,
  { expectedQuotes, asyncRequestId, quotesKey }: { expectedQuotes: number, asyncRequestId: string, quotesKey: string },
): QuoteState => {
  const quoteRequest = {
    ...state.getIn(['quoteRequests', quotesKey], {}),
    expectedQuotes,
    asyncRequestId,
    quotes: [],
    subscribed: null,
  }
  const subscribed = state.getIn(['quoteRequests', quotesKey, 'subscribed'], null)
  let newState = state
  if (subscribed) {
    newState = setToUnsubscribeR(state, { subscribed })
  }
  return newState
    .set('goToTab', '')
    .setIn(['quoteRequests', quotesKey], quoteRequest)
    .setIn(['timeAsyncRequest', asyncRequestId], Date.now())
}

const sortTabQuotesR = (
  state: QuoteState,
  { activeTabKey, quotes }: { activeTabKey: string, quotes: QuoteInstance },
): QuoteState => {
  let activeSorts = state.activeSorts[activeTabKey]
  if (!activeSorts) {
    activeSorts = config.getConfig('quoteListContainer.defaultSorts')
  }
  quotes = filteredDataSort(quotes, activeSorts)
  return state.setIn(['quoteRequests', activeTabKey, 'quotes'], quotes)
}

const sortQuotesR = (
  state: QuoteState,
  { quotes, quotesKey, path }: { quotes: Array<QuoteInstance>, quotesKey: string, path: string },
): QuoteState => {
  let activeSorts = state.activeSorts[quotesKey]
  if (!activeSorts) {
    activeSorts = config.getConfig('quoteListContainer.defaultSorts')
  }
  quotes = filteredDataSort(quotes, activeSorts)
  return state.setIn(['quoteRequests', quotesKey, path || 'quotes'], quotes)
}

const setSortR = (state: QuoteState, { sort }: { sort: Sort }): QuoteState => {
  let activeSorts = state.activeSorts.asMutable()
  // For multiple sort
  /*if (activeSorts[state.activeTabKey]) {
    let exist = false
    const actualSorts = activeSorts[state.activeTabKey].asMutable().map(activeSort => {
      if (activeSort.value === sort.value) {
        activeSort = sort
        exist = true
      }
      return activeSort
    })
    if (!exist) {
      actualSorts.push(sort)
    }
    activeSorts[state.activeTabKey] = actualSorts
  } else {*/
  activeSorts[state.activeTabKey] = [sort]
  //}
  return sortQuotesR(state.set('activeSorts', activeSorts), {
    quotes: state.quoteRequests[state.activeTabKey].quotes.asMutable(),
    quotesKey: state.activeTabKey,
  })
}

const removeSortR = (state: QuoteState, { sort }: { sort: Sort }): QuoteState => {
  const activeSorts = state.activeSorts.asMutable()
  if (activeSorts[state.activeTabKey]) {
    activeSorts[state.activeTabKey] = activeSorts[state.activeTabKey].filter(
      (activeSort) => activeSort.value !== sort.value,
    )
  }
  return sortQuotesR(state.set('activeSorts', activeSorts), {
    quotes: state.quoteRequests[state.activeTabKey].quotes.asMutable(),
    quotesKey: state.activeTabKey,
  })
}

const saveQuotesR = (
  state: QuoteState,
  { quotes, quotesKey, invalid }: { quotes: Array<QuoteInstance>, quotesKey: string, invalid: boolean },
): QuoteState => {
  const path = invalid ? 'invalidQuotes' : 'quotes'
  const stateQuotes = pathOr(Immutable([]), ['quoteRequests', quotesKey, path], state).asMutable()
  let newState = state
  quotes.map((quote) => {
    stateQuotes.push(quote)
    if (!invalid && Object.keys(quote.subProductToTariffName).length === 1) {
      newState = newState.setIn(['basePrices', Object.values(quote.subProductToTariffName)[0]], quote.price)
    }
  })
  return sortQuotesR(newState, { quotes: stateQuotes, quotesKey, path })
}

const removeQuoteR = (state: QuoteState, { id, quotesKey }: { id: string, quotesKey: string }): QuoteState => {
  const quotes = state.quoteRequests[quotesKey].quotes.filter((quote) => quote.id !== id)
  const selectedQuotes = state.selectedQuotes.asMutable()
  if (state.selectedQuotes[quotesKey] && state.selectedQuotes[quotesKey] === id) {
    delete selectedQuotes[quotesKey]
  }
  return state
    .setIn(['quoteRequests', quotesKey], {
      quotes,
      expectedQuotes: quotes.length,
      asyncRequestId: state.quoteRequests[quotesKey].asyncRequestId,
    })
    .set('selectedQuotes', selectedQuotes)
}

const refreshQuoteRequestSuccessR = (
  state: QuoteState,
  { newQuote, quotesKey }: { newQuote: QuoteInstance, quotesKey: string },
): QuoteState => {
  let quotes = state.quoteRequests[quotesKey].quotes.asMutable()
  quotes = quotes
    .map((quote: QuoteInstance) => {
      if (quote.uniqueQuoteId === JSON.stringify(newQuote.subProductToTariffName)) {
        return {
          ...quote,
          ...newQuote,
        }
      }
      return quote
    })
    .filter(
      (quote: QuoteInstance) =>
        Object.keys(newQuote.subProductToTariffName).length === Object.keys(quote.subProductToTariffName).length,
    )
  return saveQuotesR(state.setIn(['quoteRequests', quotesKey, 'quotes'], []), { quotes, quotesKey })
}

const quoteSetSpecificationR = (
  state: QuoteState,
  { quoteId, specification }: { quoteId: string, specification: ExpandedSpecificationDTO },
): QuoteState => state.setIn(['quoteSpecifications', quoteId], specification)

const quoteContentRequestSuccessR = (
  state: QuoteState,
  { quoteContent }: { quoteContent: ProductContent },
): QuoteState => {
  // $FlowIssue
  let specification: ExpandedSpecificationDTO = {}
  if (Object.keys(state.quoteSpecifications).length) {
    // $FlowIssue
    specification = Object.values(state.quoteSpecifications)[0]
  }
  if (!quoteContent.fieldOrder) {
    return state
  }
  return generateTextsFromSpecification(state, { content: quoteContent, specification, key: 'quoteTexts' }).merge({
    quoteContent,
  })
}

const quoteSetValidatorR = (
  state: QuoteState,
  { calculatorName, validator }: { calculatorName: string, validator: FieldValidator },
): QuoteState => {
  let newState: QuoteState = state
  if (!state.quoteSpecifications[calculatorName]) {
    newState = quoteSetSpecificationR(state, {
      quoteId: calculatorName,
      specification: validator.getActualSpecification(),
    })
  }
  return quoteContentRequestSuccessR(newState, { quoteContent: state.quoteContent }).setIn(
    ['quoteValidators', calculatorName],
    validator,
  )
}

const onChangeActiveTabR = (state: QuoteState, { activeTabKey }: { activeTabKey: string }): QuoteState =>
  state.set('activeTabKey', activeTabKey)

const onChangeGoToTabR = (state: QuoteState, { goToTab }: { goToTab: string }): QuoteState =>
  state.set('goToTab', goToTab)

const onAddToEmailQuotesR = (state: QuoteState, { quote }: { quote: QuoteInstance }): QuoteState => {
  const sendToEmailQuotes = state.sendToEmailQuotes.asMutable()
  for (let index = 0; index < sendToEmailQuotes.length; index++) {
    if (
      JSON.stringify(sendToEmailQuotes[index].subProductToTariffName) === JSON.stringify(quote.subProductToTariffName)
    ) {
      return state
    }
  }
  sendToEmailQuotes.push(quote)
  return state.merge({ sendToEmailQuotes })
}

const onRemoveFromEmailQuotesR = (state: QuoteState, { index }: { index: number }): QuoteState => {
  const sendToEmailQuotes = state.sendToEmailQuotes.asMutable()
  sendToEmailQuotes.splice(index, 1)
  return state.merge({ sendToEmailQuotes })
}

const onSelectQuoteR = (
  state: QuoteState,
  { productName, quote }: { productName: string, quote: QuoteInstance },
): QuoteState =>
  state
    .setIn(['selectedQuotes'], quote.subProductToTariffName)
    .setIn(
      ['quoteRequests', config.getConfig('quoteListContainer.quoteDependsOn')[productName], 'quoteRequestData'],
      createQuoteRequestData(productName, quote),
    )

const onRemoveQuoteR = (
  state: QuoteState,
  { productName, quoteId }: { productName: string, quoteId: string },
): QuoteState => {
  const selectedQuotes = state.selectedQuotes.asMutable()
  const selectedCoveragePrices = state.selectedCoveragePrices.asMutable()
  delete selectedQuotes[productName]
  delete selectedCoveragePrices[quoteId]
  return state
    .set('selectedQuotes', selectedQuotes)
    .set('selectedCoveragePrices', selectedCoveragePrices)
    .setIn(
      ['quoteRequests', config.getConfig('quoteListContainer.quoteDependsOn')[productName], 'quoteRequestData'],
      createQuoteRequestData(productName),
    )
}

const setFilterR = (
  state: QuoteState,
  {
    tab,
    name,
    value,
    multiSelect,
    temporary,
  }: { tab: string, name: string, value: string, multiSelect: boolean, temporary: boolean },
): QuoteState => {
  const key = temporary ? 'temporaryFilters' : 'filters'
  if (!multiSelect) {
    return state.setIn([key, tab, name], [value])
  }
  const filters = state.getIn([key, tab, name], Immutable([])).asMutable()
  if (filters.includes(value)) {
    filters.splice(filters.indexOf(value), 1)
  } else {
    filters.push(value)
  }
  return state.setIn([key, tab, name], filters)
}

const resetFiltersR = (state: QuoteState): QuoteState =>
  state.merge({ filters: INITIAL_STATE.filters, temporaryFilters: INITIAL_STATE.temporaryFilters })

const getCoverageLimitsRequestSuccessR = (state: QuoteState, { calculatorName, data }) =>
  state.setIn(['coverageLimits', calculatorName], data)

const getAllCoverageLimitsRequestSuccessR = (state: QuoteState, { data }: { data: Object }) =>
  state.set('coverageLimits', data)

const getQuoteSupplementaryInsurancePriceRequestR = (
  state: QuoteState,
  { data, quote }: { data: Object, quote: QuoteInstance },
) => state.setIn(['lastSupplementaryInsuranceRequestData', quote.uniqueQuoteId], JSON.stringify(data))

const getQuoteSupplementaryInsurancePriceRequestSuccessR = (
  state: QuoteState,
  { newQuote, oldQuote }: { newQuote: QuoteInstance, oldQuote: QuoteInstance },
) => {
  let newState = state
  Object.keys(state.quoteRequests).forEach((quoteRequestKey) => {
    const quoteIndex = newState
      .getIn(['quoteRequests', quoteRequestKey, 'quotes'], [])
      .findIndex((quote) => oldQuote.id === quote.id)
    if (quoteIndex >= 0) {
      newState = newState.setIn(
        ['quoteRequests', quoteRequestKey, 'quotes', quoteIndex, 'properties'],
        Immutable.merge(
          newState.getIn(['quoteRequests', quoteRequestKey, 'quotes', quoteIndex, 'properties'], {}),
          newQuote.properties || {},
        ),
      )
    }
  })
  return newState.setIn(['selectedCoveragePrices', JSON.stringify(newQuote.subProductToTariffName)], {
    fullPrice: newQuote.price,
    coveragesPrice: newQuote.price - oldQuote.price,
  })
}

const setQuoteRestrictionsR = (state: QuoteState, { quoteRestrictions }) =>
  state.set('quoteRestrictions', quoteRestrictions)

const setQuoteFiltersR = (state: QuoteState, { filters, key }: { filters: QuoteFilterBlockType, key: string }) =>
  state.set(key || 'filters', filters)

const setQuoteRequestDataCalculatorsR = (
  state: QuoteState,
  { enabledCalculators, quoteKeys }: { enabledCalculators: Array<string>, quoteKeys?: Array<string> },
) => {
  quoteKeys = quoteKeys ? quoteKeys : Object.keys(state.quoteRequests)
  const resetFor = Object.keys(state.quoteRequests).filter((quoteKey) => !quoteKeys.includes(quoteKey))
  let quoteRequests = Object.keys(state.quoteRequests).reduce(
    (acc, quoteKey) =>
      acc.setIn(
        [quoteKey, 'quoteRequestData', 'enabledCalculators'],
        resetFor.includes(quoteKey) ? [] : enabledCalculators,
      ),
    state.quoteRequests,
  )
  return state.set('quoteRequests', quoteRequests)
}

const setSelectedQuotesR = (state: QuoteState, { selectedQuotes }: { selectedQuotes: Object }) =>
  state.set('selectedQuotes', selectedQuotes)

const clearCurrentQuoteDataR = (
  state: QuoteState,
  { quoteKeys, tabKey }: { quoteKeys: Array<string>, tabKey: string },
) =>
  quoteKeys
    .reduce(
      (acc, quoteKey) =>
        acc.setIn(['quoteRequests', quoteKey, 'currentQuoteRequestData'], {}).set('signalToChangeTab', tabKey),
      state,
    )
    .set('basePrices', INITIAL_STATE.basePrices)

const setSignalToChangeTabR = (state: QuoteState, { tab }: { tab: string }) => state.set('signalToChangeTab', tab)

const resetLastSupplementaryInsuranceRequestDataR = (state: QuoteState, { quoteId, data }: { quoteId: string }) => {
  let lastSupplementaryInsuranceRequestData = state.lastSupplementaryInsuranceRequestData
  if (!lastSupplementaryInsuranceRequestData[quoteId]) {
    return state
  } else {
    lastSupplementaryInsuranceRequestData = JSON.parse(lastSupplementaryInsuranceRequestData[quoteId])
    lastSupplementaryInsuranceRequestData.productDetailValues = data
  }
  return state.setIn(
    ['lastSupplementaryInsuranceRequestData', quoteId],
    JSON.stringify(lastSupplementaryInsuranceRequestData),
  )
}

const setQuoteRequestsR = (state: QuoteState, { quoteRequests }) => state.set('quoteRequests', quoteRequests)

const setSelectedCoveragePricesR = (state: QuoteState, { selectedCoveragePrices }) =>
  state.set('selectedCoveragePrices', selectedCoveragePrices)

/* ------------- Hookup Reducers To Types ------------- */

export const reducer = createReducer(INITIAL_STATE, {
  [Types.QUOTE_ASYNC_REQUEST]: quoteAsyncRequestR,
  [Types.QUOTE_ASYNC_REQUEST_SUCCESS]: quoteAsyncRequestSuccessR,
  [Types.SORT_TAB_QUOTES]: sortTabQuotesR,
  [Types.SORT_QUOTES]: sortQuotesR,
  [Types.SET_SORT]: setSortR,
  [Types.REMOVE_SORT]: removeSortR,
  [Types.SAVE_QUOTES]: saveQuotesR,
  [Types.REMOVE_QUOTE]: removeQuoteR,
  [Types.RESET_QUOTES]: resetQuotesR,
  [Types.GET_QUOTE_REQUEST]: (state) => state,
  [Types.REFRESH_QUOTE_REQUEST_SUCCESS]: refreshQuoteRequestSuccessR,
  [Types.GET_QUOTE_SUPPLEMENTARY_INSURANCE_PRICE_REQUEST]: getQuoteSupplementaryInsurancePriceRequestR,
  [Types.GET_QUOTE_SUPPLEMENTARY_INSURANCE_PRICE_REQUEST_SUCCESS]: getQuoteSupplementaryInsurancePriceRequestSuccessR,
  [Types.QUOTE_SET_VALIDATOR]: quoteSetValidatorR,
  [Types.QUOTE_SET_SPECIFICATION]: quoteSetSpecificationR,
  [Types.QUOTE_CONTENT_REQUEST]: (state) => state,
  [Types.QUOTE_CONTENT_REQUEST_SUCCESS]: quoteContentRequestSuccessR,
  [Types.ON_CHANGE_ACTIVE_TAB]: onChangeActiveTabR,
  [Types.ON_CHANGE_GO_TO_TAB]: onChangeGoToTabR,
  [Types.ON_SELECT_QUOTE]: onSelectQuoteR,
  [Types.ON_REMOVE_QUOTE]: onRemoveQuoteR,
  [Types.SET_FILTER]: setFilterR,
  [Types.RESET_FILTERS]: resetFiltersR,
  [Types.GET_COVERAGE_LIMITS_REQUEST]: (state) => state,
  [Types.GET_COVERAGE_LIMITS_REQUEST_SUCCESS]: getCoverageLimitsRequestSuccessR,
  [Types.GET_ALL_COVERAGE_LIMITS_REQUEST]: (state) => state,
  [Types.GET_ALL_COVERAGE_LIMITS_REQUEST_SUCCESS]: getAllCoverageLimitsRequestSuccessR,
  [Types.CHANGE_STOMP_SUBSCRIBED]: changeStompSubscribedR,
  [Types.SET_TO_UNSUBSCRIBE]: setToUnsubscribeR,
  [Types.SET_STOMP_CONNECTED]: setStompConnectedR,
  [Types.ON_ADD_TO_EMAIL_QUOTES]: onAddToEmailQuotesR,
  [Types.ON_REMOVE_FROM_EMAIL_QUOTES]: onRemoveFromEmailQuotesR,
  [Types.SET_QUOTE_RESTRICTIONS]: setQuoteRestrictionsR,
  [Types.SET_QUOTE_FILTERS]: setQuoteFiltersR,
  [Types.SET_QUOTE_REQUEST_DATA_CALCULATORS]: setQuoteRequestDataCalculatorsR,
  [Types.SET_SELECTED_QUOTES]: setSelectedQuotesR,
  [Types.CLEAR_CURRENT_QUOTE_DATA]: clearCurrentQuoteDataR,
  [Types.SET_SIGNAL_TO_CHANGE_TAB]: setSignalToChangeTabR,
  [Types.RESET_LAST_SUPPLEMENTARY_INSURANCE_REQUEST_DATA]: resetLastSupplementaryInsuranceRequestDataR,
  [Types.SET_QUOTE_REQUESTS]: setQuoteRequestsR,
  [Types.SET_SELECTED_COVERAGE_PRICES]: setSelectedCoveragePricesR,
})
